import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { setActiveSpace, showCreateSpaceModal } from '../../Manage/actions'
import useCheckScreenSize from '../../hooks/checkMobileScreen'
import SpaceSelectionModal from './components/SpaceSelectionModal/SpaceSelectionModal'
import styles from './SpacesTabs.module.css'
import MoreDropdown from './components/SpaceDropdown/SpaceDropdown'

const SpacesTabs = ({ sites = [], onTabChange, activeTab, siteLimit, hideSpacesProjects }) => {
  const dispatch = useDispatch()
  const { spaces } = useSelector((state) => state.manage)
  const [mobileModal, setMobileModal] = useState(null)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const { isMobile } = useCheckScreenSize()

  const filteredSites = hideSpacesProjects ? sites.filter((site) => !site.spaceId) : sites

  const sitesBySpaceId = sites.reduce((acc, site) => {
    if (site.spaceId) (acc[site.spaceId] ||= []).push(site)
    return acc
  }, {})

  const getValidTimestamp = (date) => {
    const timestamp = new Date(date || 0).getTime()
    return isNaN(timestamp) ? 0 : timestamp
  }

  const sortedSpaces = spaces
    .map((space) => {
      const spaceSites = sitesBySpaceId[space.spaceId] || []
      const latestProjectModified = spaceSites.reduce(
        (max, site) => Math.max(max, getValidTimestamp(site.updatedAt || site.created)),
        0
      )
      const spaceTimestamp = getValidTimestamp(space.created)

      return {
        key: String(space.spaceId),
        label: space.name,
        count: spaceSites.length,
        isSpace: true,
        fullName: space.name,
        latestModified: Math.max(spaceTimestamp, latestProjectModified)
      }
    })
    .sort((a, b) => b.latestModified - a.latestModified)
  const visibleSpaces = sortedSpaces.slice(0, 4)
  const hiddenSpaces = sortedSpaces.slice(4)

  const isAnyHiddenSpaceActive = hiddenSpaces.some((space) => space.key === activeTab)

  const staticTabs = [
    {
      key: 'all',
      label: 'All',
      count: filteredSites.length,
      isDefault: true
    },
    {
      key: 'deleted',
      label: 'Trash',
      count: null,
      isDeleted: true,
      icon: isMobile ? null : '/assets/icons/trash.svg',
      tooltip: 'Trash'
    },
    {
      key: 'archive',
      label: 'Archive',
      count: null,
      isArchive: true,
      icon: isMobile ? null : '/assets/icons/archive-box.svg',
      tooltip: 'Archive'
    }
  ]

  const handleTabClick = (tabKey) => {
    if (tabKey === 'add-space') {
      dispatch(showCreateSpaceModal(true))
      return
    }

    setIsMoreOpen(false)
    dispatch(setActiveSpace(tabKey === 'all' ? null : tabKey))
    onTabChange(tabKey)
  }

  const handleAddSpaceClick = () => {
    dispatch(showCreateSpaceModal(true))
  }

  const renderTabContent = (tab) => {
    if (tab.isSpace) {
      const content = (
        <div className={styles.tabContent}>
          <span className={styles.tabLabel}>
            {tab.label.length > 12 ? `${tab.label.substring(0, 12)}...` : tab.label}
          </span>
          {tab.count !== null && <span className={styles.tabCount}>{tab.count}</span>}
        </div>
      )

      const wrappedContent =
        tab.label.length > 12 ? (
          <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${tab.key}`}>{tab.fullName}</Tooltip>}>
            {content}
          </OverlayTrigger>
        ) : (
          content
        )

      return wrappedContent
    }

    return (
      <div className={styles.tabContent}>
        {tab.icon ? (
          <>
            {tab.isDeleted || tab.isArchive ? (
              <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${tab.key}`}>{tab.tooltip}</Tooltip>}>
                <img src={tab.icon} alt={tab.label} className={styles.tabIcon} />
              </OverlayTrigger>
            ) : (
              <img src={tab.icon} alt={tab.label} className={styles.tabIcon} />
            )}
            <span className={styles.srOnly}>{tab.label}</span>
          </>
        ) : (
          <span className={styles.tabLabel}>{tab.label}</span>
        )}
        {tab.count !== null && !tab.isArchive && !tab.isDeleted && (
          <span className={styles.tabCount}>
            {tab.key === 'all' && siteLimit !== 999 ? `${tab.count}/${siteLimit}` : tab.count}
          </span>
        )}
      </div>
    )
  }

  if (isMobile) {
    const isViewingSpace = activeTab !== 'all' && activeTab !== 'archive' && activeTab !== 'deleted'

    return (
      <div className={styles.tabsContainer}>
        <div className={styles.tabsList}>
          <button
            className={`${styles.tab} ${activeTab === 'all' ? styles.tabActive : ''} ${styles.tabDefault}`}
            onClick={() => handleTabClick('all')}
          >
            {renderTabContent(staticTabs[0])}
          </button>

          {sortedSpaces.length > 0 && (
            <button
              className={`${styles.tab} ${isViewingSpace ? styles.tabActive : ''}`}
              onClick={() => setMobileModal('spaces')}
            >
              <div className={styles.tabContent}>
                <span className={styles.tabLabel}>Your Spaces</span>
                <img src="/assets/icons/chevron-down.svg" alt="" className={styles.dropdownChevron} />
              </div>
            </button>
          )}

          {sortedSpaces.length === 0 && (
            <button className={`${styles.tab} ${styles.addTab}`} onClick={handleAddSpaceClick} title="Create new space">
              <div className={styles.tabContent}>
                <img
                  src="assets/icons/plus-square.svg"
                  alt="Add space"
                  width="22"
                  height="22"
                  className={styles.addIcon}
                />
              </div>
            </button>
          )}
        </div>

        <button className={styles.moreButtonMobile} onClick={() => setMobileModal('more')}>
          More
        </button>

        <SpaceSelectionModal
          isOpen={!!mobileModal}
          variant={mobileModal || 'spaces'}
          onClose={() => setMobileModal(null)}
          spaces={spaces}
          activeTab={activeTab}
          onTabChange={(tabKey) => {
            setMobileModal(null)
            handleTabClick(tabKey)
          }}
          siteLimit={siteLimit}
          sites={sites}
        />
      </div>
    )
  }

  return (
    <div className={styles.tabsContainer}>
      <div className={styles.tabsList}>
        <button
          className={`${styles.tab} ${activeTab === 'all' && !isAnyHiddenSpaceActive && !isMoreOpen ? styles.tabActive : ''} ${styles.tabDefault}`}
          onClick={() => handleTabClick('all')}
        >
          {renderTabContent(staticTabs[0])}
        </button>

        {visibleSpaces.map((tab) => {
          const isThisTabActive = activeTab === tab.key && !isAnyHiddenSpaceActive && !isMoreOpen

          return (
            <button
              key={tab.key}
              className={`${styles.tab} ${isThisTabActive ? styles.tabActive : ''}`}
              onClick={() => handleTabClick(tab.key)}
            >
              {renderTabContent(tab)}
            </button>
          )
        })}

        {hiddenSpaces.length > 0 && (
          <MoreDropdown
            spaces={hiddenSpaces}
            activeTab={activeTab}
            onTabChange={handleTabClick}
            isOpen={isMoreOpen}
            onToggle={() => setIsMoreOpen(!isMoreOpen)}
          />
        )}

        <button className={`${styles.tab} ${styles.addTab}`} onClick={handleAddSpaceClick} title="Create new space">
          <div className={styles.tabContent}>
            <img src="assets/icons/plus-square.svg" alt="Add space" width="22" height="22" className={styles.addIcon} />
          </div>
        </button>
      </div>

      <div className={styles.archiveContainer}>
        {staticTabs.slice(1).map((tab) => {
          const isActive = activeTab === tab.key && !isAnyHiddenSpaceActive && !isMoreOpen

          return (
            <button
              key={tab.key}
              data-testid={`${tab.key}-tab`}
              className={`${styles.tab} ${styles.archiveTab} ${isActive ? styles.tabActive : ''}`}
              onClick={() => handleTabClick(tab.key)}
            >
              {renderTabContent(tab)}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default SpacesTabs
